import * as React from 'react'
import { useDiscDiagnostic } from '@/lib/api/company/chartScan'
import { parseChartIso } from '@/modules/imports/components/TachographChartCanvas'
import { fmt, useChartT } from '../i18n'

type Props = {
  companyId: string | number
  discId: number
  onClose: () => void
}

const TYPE_LABELS = ['Rest', 'Avail', 'Work', 'Drive', 'NoData']
const TYPE_BG = ['#cbd5e1', '#fef3c7', '#bfdbfe', '#fecaca', '#e5e7eb']

function fmtMinute(m: number) {
  const h = Math.floor(m / 60)
  const min = m % 60
  return `${h.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`
}

// Per-minute diagnostic dump showing what the radial scan returned for every
// minute of the chart. The grid is 24 rows (one per hour) x 60 columns (one
// per minute). Each cell shows the pixel run length and is tinted by its
// derived activity type. Used to diff against WPF's TachographChartReader
// internals when activities differ.
export function DiagnosticPanel({ companyId, discId, onClose }: Props) {
  const T = useChartT()
  const { data, isLoading, error } = useDiscDiagnostic(companyId, discId, true)

  return (
    <div
      className="tw-fixed tw-inset-0 tw-z-[2000] tw-flex tw-items-center tw-justify-center tw-bg-black/50"
      onClick={onClose}
    >
      <div
        className="tw-flex tw-h-[90vh] tw-w-[95vw] tw-max-w-[1400px] tw-flex-col tw-gap-3 tw-rounded tw-bg-white tw-p-4 tw-shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-slate-200 tw-pb-2">
          <h2 className="tw-text-lg tw-font-semibold">
            {fmt(T('diagnosticTitle', 'Diagnostyka tarczki #{0}'), discId)}
          </h2>
          <div className="tw-flex tw-items-center tw-gap-3 tw-text-xs">
            {TYPE_LABELS.slice(0, 4).map((label, idx) => (
              <span key={label} className="tw-flex tw-items-center tw-gap-1">
                <span
                  className="tw-inline-block tw-h-3 tw-w-3 tw-rounded-sm tw-border tw-border-slate-400"
                  style={{ background: TYPE_BG[idx] }}
                />
                {label} ({idx === 0 ? '≤8' : idx === 1 ? '9-14' : idx === 2 ? '15-22' : '>22'})
              </span>
            ))}
            <button
              onClick={onClose}
              className="tw-rounded tw-border tw-border-slate-300 tw-px-2 tw-py-1"
            >
              {T('close', 'Zamknij')}
            </button>
          </div>
        </header>

        {isLoading && (
          <div className="tw-text-sm tw-text-slate-500">{T('loading', 'Ładowanie...')}</div>
        )}
        {error && (
          <div className="tw-text-sm tw-text-red-600">
            {fmt(T('error', 'Błąd: {0}'), String((error as Error).message))}
          </div>
        )}

        {data && (
          <div className="tw-flex tw-flex-1 tw-gap-4 tw-overflow-hidden">
            <section className="tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden">
              <h3 className="tw-mb-1 tw-text-sm tw-font-semibold">
                MinutesToActivitySize (1440 minut)
              </h3>
              <div className="tw-flex-1 tw-overflow-auto tw-rounded tw-border tw-border-slate-200">
                <table className="tw-w-full tw-border-collapse tw-text-[10px] tw-font-mono">
                  <thead className="tw-sticky tw-top-0 tw-z-10 tw-bg-slate-100">
                    <tr>
                      <th className="tw-border tw-border-slate-200 tw-p-1 tw-text-right">h\m</th>
                      {Array.from({ length: 60 }, (_, m) => (
                        <th
                          key={m}
                          className="tw-border tw-border-slate-200 tw-p-1 tw-text-center tw-text-slate-500"
                        >
                          {m}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: 24 }, (_, h) => (
                      <tr key={h}>
                        <td className="tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-1 tw-text-right tw-font-semibold">
                          {h.toString().padStart(2, '0')}
                        </td>
                        {Array.from({ length: 60 }, (_, m) => {
                          const idx = h * 60 + m
                          const size = data.minutesToActivitySize[idx] ?? 0
                          const t = data.minutesToActivityType[idx] ?? 0
                          return (
                            <td
                              key={m}
                              className="tw-border tw-border-slate-200 tw-p-0.5 tw-text-center"
                              style={{ background: TYPE_BG[t] }}
                              title={`${fmtMinute(idx)} | runLen=${size} | ${TYPE_LABELS[t]}`}
                            >
                              {size}
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <aside className="tw-flex tw-w-80 tw-flex-col tw-overflow-hidden">
              <h3 className="tw-mb-1 tw-text-sm tw-font-semibold">
                {fmt(T('afterMerge', 'Po scaleniu ({0})'), data.mergedActivities.length)}
              </h3>
              <div className="tw-flex-1 tw-overflow-auto tw-rounded tw-border tw-border-slate-200">
                <table className="tw-w-full tw-border-collapse tw-text-xs">
                  <thead className="tw-sticky tw-top-0 tw-bg-slate-100">
                    <tr>
                      <th className="tw-border tw-border-slate-200 tw-p-1 tw-text-left">
                        {T('colType', 'Typ')}
                      </th>
                      <th className="tw-border tw-border-slate-200 tw-p-1 tw-text-left">
                        {T('colFromShort', 'Od')}
                      </th>
                      <th className="tw-border tw-border-slate-200 tw-p-1 tw-text-left">
                        {T('colToShort', 'Do')}
                      </th>
                      <th className="tw-border tw-border-slate-200 tw-p-1 tw-text-right">
                        {T('colMinutes', 'Min')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.mergedActivities.map((a) => {
                      const start = parseChartIso(a.startTime)
                      const end = parseChartIso(a.endTime)
                      const dur = Math.round((end.getTime() - start.getTime()) / 60_000)
                      return (
                        <tr key={a.activityId}>
                          <td
                            className="tw-border tw-border-slate-200 tw-p-1"
                            style={{ background: TYPE_BG[a.type] }}
                          >
                            {TYPE_LABELS[a.type]}
                          </td>
                          <td className="tw-border tw-border-slate-200 tw-p-1 tw-font-mono">
                            {start.toISOString().substring(11, 16)}
                          </td>
                          <td className="tw-border tw-border-slate-200 tw-p-1 tw-font-mono">
                            {end.toISOString().substring(11, 16)}
                          </td>
                          <td className="tw-border tw-border-slate-200 tw-p-1 tw-text-right tw-font-mono">
                            {dur}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  )
}
